import { useParams, Link } from 'react-router-dom';
import { useEffect, useLayoutEffect } from 'react';
import { getAppById } from '../data/apps';
import Footer from '../components/Footer';
import MobileScreenshotsGallery from '../components/MobileScreenshotsGallery';
import WebAdminScreenshotsGallery from '../components/WebAdminScreenshotsGallery';
import ControllerScreenshotsGallery from '../components/ControllerScreenshotsGallery';
import SEO from '../components/SEO';

const AppDetailPage = () => {
  const { appId } = useParams<{ appId: string }>();
  const app = appId ? getAppById(appId) : undefined;

  // Scroller vers le haut lors du chargement de la page
  // Utilisation de useLayoutEffect pour s'exécuter avant le paint
  useLayoutEffect(() => {
    // Scroll immédiat sans animation
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [appId]);

  // Backup avec setTimeout au cas où useLayoutEffect ne suffit pas
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // Scroll immédiat
    scrollToTop();
    
    // Scroll après un court délai pour s'assurer que tout est rendu
    const timeout = setTimeout(scrollToTop, 0);
    
    return () => clearTimeout(timeout);
  }, [appId]);

  if (!app) {
    return (
      <>
        <SEO
          title="Application non trouvée - Tunisie Parking"
          description="L'application que vous recherchez n'existe pas."
          noindex={true}
        />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Application non trouvée</h1>
          <p className="text-gray-600 mb-8">L'application que vous recherchez n'existe pas.</p>
          <Link
            to="/"
            className="px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${app.name} - Tunisie Parking | ${app.fullDescription}`}
        description={app.fullDescription}
        keywords={`${app.name.toLowerCase()}, parking tunisie, ${app.id === 'mobile' ? 'application mobile parking' : app.id === 'controller' ? 'application contrôleur parking' : 'web admin parking'}, tunisie parking`}
        url={`https://parking.tn/apps/${app.id}`}
      />
      <div className="min-h-screen bg-gray-50">
      {/* Header avec navigation */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className={`bg-gradient-to-br ${app.color} text-white py-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-8xl mb-6">{app.icon}</div>
            <h1 className="text-5xl sm:text-6xl font-bold mb-4">{app.name}</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
              {app.fullDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {app.id === 'mobile' ? 'Parcours utilisateur complet' : app.id === 'web-admin' ? 'Parcours administrateur complet' : app.id === 'controller' ? 'Parcours contrôleur complet' : 'Captures d\'écran'}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {app.id === 'mobile' 
                ? 'Découvrez le parcours complet de l\'application mobile, de la découverte des parkings à la gestion de votre compte.'
                : app.id === 'web-admin'
                ? 'Découvrez le parcours complet de l\'interface d\'administration, du dashboard analytics à la configuration système.'
                : app.id === 'controller'
                ? 'Découvrez le parcours complet de l\'application contrôleur, du scan de plaques à la gestion des contrôles.'
                : `Découvrez l'interface et les fonctionnalités de ${app.name}`}
            </p>
          </div>

          {app.id === 'mobile' ? (
            <MobileScreenshotsGallery />
          ) : app.id === 'web-admin' ? (
            <WebAdminScreenshotsGallery />
          ) : app.id === 'controller' ? (
            <ControllerScreenshotsGallery />
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-8`}>
              <div className="text-center text-gray-500">
                <p>Aucune capture d'écran disponible</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Fonctionnalités complètes
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Découvrez toutes les fonctionnalités qui font de cette application un outil puissant et intuitif.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {app.features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-200"
              >
                <div className="flex items-start">
                  <div className="text-4xl mr-4">{feature.icon}</div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Cas d'utilisation
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Voici quelques exemples concrets d'utilisation de cette application.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {app.useCases.map((useCase, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border-l-4 border-primary-500"
              >
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-primary-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <p className="text-gray-700">{useCase}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 bg-white hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Technologies utilisées
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Cette application est construite avec les dernières technologies pour garantir performance et fiabilité.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {app.technologies.map((tech, index) => (
              <span
                key={index}
                className={`px-6 py-3 bg-gradient-to-r ${app.color} text-white font-semibold rounded-lg shadow-md`}
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>


      <Footer />
    </div>
    </>
  );
};

export default AppDetailPage;

